import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalHotels: 0,
    activeHotels: 0,
    totalSpas: 0,
    activeSpas: 0,
    totalCustomers: 0,
    activeCustomers: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    totalNotifications: 0,
    totalDestinations: 0,
    loading: true
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setStats(prev => ({ ...prev, loading: true }));
      
      const [
        hotelsRes,
        spasRes,
        customersRes,
        subscriptionsRes,
        notificationsRes,
        destinationsRes
      ] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/admin/hotels?limit=1000`),
        axios.get(`${API_BASE_URL}/admin/spas?limit=1000`),
        axios.get(`${API_BASE_URL}/admin/customers?limit=1000`),
        axios.get(`${API_BASE_URL}/admin/subscription-history?limit=1000`),
        axios.get(`${API_BASE_URL}/admin/push-notifications?limit=1000`),
        axios.get(`${API_BASE_URL}/admin/suggested-destinations?limit=1000`)
      ]);

      const totalHotels = hotelsRes.status === 'fulfilled' ? (hotelsRes.value.data.total || hotelsRes.value.data.count || hotelsRes.value.data.data?.length || 0) : 0;
      const activeHotels = hotelsRes.status === 'fulfilled' 
        ? (hotelsRes.value.data.data?.filter(h => h.status === 'active').length || 0) 
        : 0;

      const totalSpas = spasRes.status === 'fulfilled' ? (spasRes.value.data.total || spasRes.value.data.count || spasRes.value.data.data?.length || 0) : 0;
      const activeSpas = spasRes.status === 'fulfilled' 
        ? (spasRes.value.data.data?.filter(s => s.status === 'active').length || 0) 
        : 0;

      const totalCustomers = customersRes.status === 'fulfilled' ? (customersRes.value.data.total || customersRes.value.data.count || 0) : 0;
      const activeCustomers = customersRes.status === 'fulfilled' 
        ? (customersRes.value.data.data?.filter(c => c.isActive).length || 0) 
        : 0;

      const totalSubscriptions = subscriptionsRes.status === 'fulfilled' ? (subscriptionsRes.value.data.total || subscriptionsRes.value.data.count || 0) : 0;
      const activeSubscriptions = subscriptionsRes.status === 'fulfilled' 
        ? (subscriptionsRes.value.data.data?.filter(s => s.status === 'active').length || 0) 
        : 0;

      const totalNotifications = notificationsRes.status === 'fulfilled' ? (notificationsRes.value.data.count || notificationsRes.value.data.total || 0) : 0;
      const totalDestinations = destinationsRes.status === 'fulfilled' ? (destinationsRes.value.data.total || destinationsRes.value.data.count || 0) : 0;

      setStats({
        totalHotels,
        activeHotels,
        totalSpas,
        activeSpas,
        totalCustomers,
        activeCustomers,
        totalSubscriptions,
        activeSubscriptions,
        totalNotifications,
        totalDestinations,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  if (stats.loading) {
    return (
      <Layout>
        <div className="container">
          <div className="dashboard-header">
            <div className="skeleton skeleton-text" style={{ width: '200px', height: '40px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-button" style={{ width: '160px', height: '40px', borderRadius: '8px' }}></div>
          </div>

          <div className="stats-grid">
            {[1, 2, 3, 4, 5, 6].map((index) => (
              <div key={index} className="stat-card">
                <div className="skeleton skeleton-text" style={{ width: '100px', height: '56px', borderRadius: '8px', margin: '0 auto 12px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '120px', height: '20px', borderRadius: '4px', margin: '0 auto' }}></div>
              </div>
            ))}
          </div>

          <div className="quick-actions">
            <div className="skeleton skeleton-text" style={{ width: '150px', height: '32px', borderRadius: '8px', marginBottom: '20px' }}></div>
            <div className="action-buttons">
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <div key={index} className="skeleton skeleton-button" style={{ width: '140px', height: '44px', borderRadius: '8px' }}></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>Dashboard</h1>
            <p className="dashboard-subtitle">Welcome back! Here's what's happening with your business.</p>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="stats-grid">
          <Link to="/sy-admin/hotels" className="stat-card stat-card-link">
            <div className="stat-icon">🏢</div>
            <h2>{stats.totalHotels}</h2>
            <p>Total Hotels</p>
            <span className="stat-badge stat-badge-active">{stats.activeHotels} Active</span>
          </Link>

          <Link to="/sy-admin/spas" className="stat-card stat-card-link">
            <div className="stat-icon">💆</div>
            <h2>{stats.totalSpas}</h2>
            <p>Total Spas</p>
            <span className="stat-badge stat-badge-active">{stats.activeSpas} Active</span>
          </Link>

          <Link to="/sy-admin/customers" className="stat-card stat-card-link">
            <div className="stat-icon">👥</div>
            <h2>{stats.totalCustomers}</h2>
            <p>Total Customers</p>
            <span className="stat-badge stat-badge-active">{stats.activeCustomers} Active</span>
          </Link>

          <Link to="/sy-admin/subscription-history" className="stat-card stat-card-link">
            <div className="stat-icon">📋</div>
            <h2>{stats.totalSubscriptions}</h2>
            <p>Subscriptions</p>
            <span className="stat-badge stat-badge-active">{stats.activeSubscriptions} Active</span>
          </Link>

          <Link to="/sy-admin/push-notifications" className="stat-card stat-card-link">
            <div className="stat-icon">🔔</div>
            <h2>{stats.totalNotifications}</h2>
            <p>Notifications</p>
            <span className="stat-badge">Total Sent</span>
          </Link>

          <Link to="/sy-admin/suggested-destinations" className="stat-card stat-card-link">
            <div className="stat-icon">📍</div>
            <h2>{stats.totalDestinations}</h2>
            <p>Destinations</p>
            <span className="stat-badge">Featured</span>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <Link to="/sy-admin/hotels/new" className="action-card">
              <span className="action-icon">➕</span>
              <div>
                <h3>Add Hotel</h3>
                <p>Create a new hotel listing</p>
              </div>
            </Link>

            <Link to="/sy-admin/spas/new" className="action-card">
              <span className="action-icon">➕</span>
              <div>
                <h3>Add Spa</h3>
                <p>Create a new spa listing</p>
              </div>
            </Link>

            <Link to="/sy-admin/push-notifications/new" className="action-card">
              <span className="action-icon">🔔</span>
              <div>
                <h3>Send Notification</h3>
                <p>Send push notification to users</p>
              </div>
            </Link>

            <Link to="/sy-admin/legal-pages/new" className="action-card">
              <span className="action-icon">📄</span>
              <div>
                <h3>Create Legal Page</h3>
                <p>Add a new legal page</p>
              </div>
            </Link>

            <Link to="/sy-admin/verification-subscriptions/new" className="action-card">
              <span className="action-icon">✓</span>
              <div>
                <h3>Verification Plan</h3>
                <p>Create verification subscription</p>
              </div>
            </Link>

            <Link to="/sy-admin/featured-subscriptions/new" className="action-card">
              <span className="action-icon">⭐</span>
              <div>
                <h3>Featured Plan</h3>
                <p>Create featured subscription</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;

